import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import RisingBubbles from "@/components/RisingBubbles";
import { ExternalLink, User } from "lucide-react";

const TeamCard = ({ m, index }) => {
    const isReal = !!m.userId;
    return (
        <div
            data-testid={`team-card-${m.role.toLowerCase().replace(/\s+/g, "-")}`}
            className="bento-card card-lift rounded-2xl border border-white/10 bg-black/60 backdrop-blur-md p-6 fade-up"
            style={{ animationDelay: `${index * 100}ms` }}
        >
            <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gradient-to-br from-blue-500/20 to-cyan-400/10 border border-white/10 flex items-center justify-center flex-shrink-0">
                    {m.avatar ? (
                        <img src={m.avatar} alt={m.username || m.role} className="w-full h-full object-cover" />
                    ) : (
                        <User className="w-7 h-7 text-slate-500" />
                    )}
                </div>
                <div className="min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-teal-400">{m.role}</div>
                    <div className="font-display text-xl font-bold text-white mt-0.5 truncate">
                        {m.displayName || m.username || (m.placeholder ? "Coming soon" : "—")}
                    </div>
                    {m.username && (
                        <div className="text-xs text-slate-400 font-mono">@{m.username}</div>
                    )}
                </div>
            </div>

            <p className="mt-4 text-sm text-slate-300 leading-relaxed">{m.bio}</p>

            <div className="mt-5">
                {isReal && m.profileUrl ? (
                    <a
                        href={m.profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`team-link-${m.userId}`}
                        className="arrow-link inline-flex items-center gap-1.5 text-sm text-teal-400 hover:text-teal-300 font-medium"
                    >
                        View Roblox profile <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-slate-500 px-2.5 py-1 rounded-full border border-white/10">
                        Placeholder
                    </span>
                )}
            </div>
        </div>
    );
};

const Team = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiClient.get("/team");
                setMembers(res.data.members || []);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div data-testid="team-page" className="relative pt-28 pb-24 overflow-hidden">
            <div aria-hidden="true" className="absolute inset-0 opacity-40">
                <RisingBubbles density={0.6} />
            </div>
            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
                <div className="max-w-2xl fade-up">
                    <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-slate-400">
                        The crew behind the current
                    </span>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight">
                        Meet the <span className="text-shimmer">team</span>
                    </h1>
                    <p className="mt-4 text-slate-300 text-base md:text-lg leading-relaxed">
                        A small crew of Roblox creators, analysts, and community builders. Every member links out to their Roblox profile.
                    </p>
                </div>

                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {loading
                        ? Array.from({ length: 4 }).map((_, i) => (
                              <div key={i} className="rounded-2xl border border-white/10 bg-[#0F0A14] h-56 animate-pulse" />
                          ))
                        : members.map((m, i) => <TeamCard key={i} m={m} index={i} />)}
                </div>
            </div>
        </div>
    );
};

export default Team;
